import React, { useState, Fragment } from "react";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  ChevronUpDownIcon,
  CheckIcon,
  SignalIcon,
  SignalSlashIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  ChatBubbleLeftIcon,
} from "@heroicons/react/24/outline";
import { useCommandRelayContext } from "./CommandRelayProvider";
import { CommandAction, OnlineUser } from "hooks/useCommandRelay";

interface ActionOption {
  value: CommandAction;
  label: string;
  icon: React.ReactNode;
  placeholder: string;
  fieldName: string;
  fieldType: "text" | "number";
}

const actionOptions: ActionOption[] = [
  {
    value: "TRACK_AWB",
    label: "Track AWB",
    icon: <MagnifyingGlassIcon className="w-5 h-5 text-blue-500" />,
    placeholder: "Enter AWB number",
    fieldName: "awbNo",
    fieldType: "text",
  },
  {
    value: "OPEN_PICKUP",
    label: "Track Pickup",
    icon: <TruckIcon className="w-5 h-5 text-green-500" />,
    placeholder: "Enter Pickup AWB (e.g., AHD12345)",
    fieldName: "pickupId",
    fieldType: "text",
  },
  {
    value: "TEXT_MESSAGE",
    label: "Text Message",
    icon: <ChatBubbleLeftIcon className="w-5 h-5 text-gray-500" />,
    placeholder: "Type your message",
    fieldName: "message",
    fieldType: "text",
  },
];

export default function CommandSenderPanel() {
  const {
    status,
    onlineUsers,
    currentUser,
    sendCommand,
    isSenderOpen,
    closeSender,
  } = useCommandRelayContext();

  const [selectedUser, setSelectedUser] = useState<OnlineUser | null>(null);
  const [selectedAction, setSelectedAction] = useState<ActionOption>(actionOptions[0]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Filter out current user from online users
  const availableUsers = onlineUsers.filter(
    (u) => u.id !== currentUser?.id
  );

  const handleSend = () => {
    if (!selectedUser || !inputValue.trim()) return;

    setIsSending(true);

    let data: Record<string, string> = {};
    data[selectedAction.fieldName] = inputValue.trim();

    const success = sendCommand({
      targetUserId: selectedUser.id,
      action: selectedAction.value,
      data,
    });

    setIsSending(false);

    if (success) {
      setInputValue("");
      // Optionally close panel after sending
      // closeSender();
    }
  };

  const handleActionChange = (action: ActionOption) => {
    setSelectedAction(action);
    setInputValue(""); // Reset input when action changes
  };

  const isConnected = status === "connected";

  return (
    <Transition.Root show={isSenderOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={closeSender}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Header */}
                    <div className="bg-indigo-700 px-4 py-6 sm:px-6">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-base font-semibold leading-6 text-white">
                          Send Command
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center gap-3">
                          {/* Connection Status */}
                          <div className="flex items-center gap-1.5">
                            {isConnected ? (
                              <>
                                <SignalIcon className="w-4 h-4 text-green-300" />
                                <span className="text-xs text-green-300">Connected</span>
                              </>
                            ) : (
                              <>
                                <SignalSlashIcon className="w-4 h-4 text-red-300" />
                                <span className="text-xs text-red-300">
                                  {status === "connecting" ? "Connecting..." : "Disconnected"}
                                </span>
                              </>
                            )}
                          </div>
                          <button
                            type="button"
                            className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white"
                            onClick={closeSender}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-indigo-300">
                          {availableUsers.length} user{availableUsers.length !== 1 ? "s" : ""} online
                        </p>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex flex-1 flex-col px-4 py-6 sm:px-6 gap-6">
                      {!isConnected ? (
                        <div className="flex-1 flex items-center justify-center">
                          <div className="text-center text-gray-500">
                            <SignalSlashIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                            <p>Not connected to command relay</p>
                            <p className="text-sm">Please check your connection</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* User Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select User ({availableUsers.length} online)
                            </label>
                            <Listbox value={selectedUser} onChange={setSelectedUser}>
                              <div className="relative">
                                <Listbox.Button className="relative w-full cursor-pointer rounded-md bg-white py-2.5 pl-3 pr-10 text-left border border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                                  {selectedUser ? (
                                    <span className="flex items-center gap-2">
                                      <UserCircleIcon className="w-5 h-5 text-gray-400" />
                                      <span className="block truncate">{selectedUser.name} (@{selectedUser.username})</span>
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">Select a user...</span>
                                  )}
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                                  </span>
                                </Listbox.Button>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {availableUsers.length === 0 ? (
                                      <div className="px-3 py-2 text-sm text-gray-500">No users online</div>
                                    ) : (
                                      availableUsers.map((user) => (
                                        <Listbox.Option
                                          key={user.id}
                                          value={user}
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                              active ? "bg-indigo-50" : ""
                                            }`
                                          }
                                        >
                                          {({ selected }) => (
                                            <>
                                              <span className="flex items-center gap-2">
                                                <UserCircleIcon className="w-5 h-5 text-gray-400" />
                                                <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                                                  {user.name} (@{user.username})
                                                </span>
                                              </span>
                                              {selected && (
                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                                                  <CheckIcon className="h-5 w-5" />
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </Listbox.Option>
                                      ))
                                    )}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                          </div>

                          {/* Action Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Action
                            </label>
                            <Listbox value={selectedAction} onChange={handleActionChange}>
                              <div className="relative">
                                <Listbox.Button className="relative w-full cursor-pointer rounded-md bg-white py-2.5 pl-3 pr-10 text-left border border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                                  <span className="flex items-center gap-2">
                                    {selectedAction.icon}
                                    <span className="block truncate">{selectedAction.label}</span>
                                  </span>
                                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                                  </span>
                                </Listbox.Button>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {actionOptions.map((action) => (
                                      <Listbox.Option
                                        key={action.value}
                                        value={action}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? "bg-indigo-50" : ""
                                          }`
                                        }
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span className="flex items-center gap-2">
                                              {action.icon}
                                              <span
                                                className={`block truncate ${
                                                  selected ? "font-semibold" : "font-normal"
                                                }`}
                                              >
                                                {action.label}
                                              </span>
                                            </span>
                                            {selected && (
                                              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                                                <CheckIcon className="h-5 w-5" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                          </div>

                          {/* Input Field */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {selectedAction.value === "TEXT_MESSAGE" ? "Message" : "Value"}
                            </label>
                            {selectedAction.value === "TEXT_MESSAGE" ? (
                              <textarea
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={selectedAction.placeholder}
                                rows={3}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 resize-none"
                              />
                            ) : (
                              <input
                                type={selectedAction.fieldType}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={selectedAction.placeholder}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                              />
                            )}
                          </div>

                          {/* Send Button */}
                          <div className="mt-auto">
                            <button
                              onClick={handleSend}
                              disabled={!selectedUser || !inputValue.trim() || isSending}
                              className="w-full flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <PaperAirplaneIcon className="w-5 h-5" />
                              {isSending ? "Sending..." : "Send Command"}
                            </button>
                            {selectedUser && (
                              <p className="mt-2 text-center text-xs text-gray-500">
                                Sending to {selectedUser.name} (@{selectedUser.username})
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
